/**
 * CambiarSucursalDialog — el AdmG elige la sucursal en la que trabaja.
 *
 * Se abre desde la tarjeta del negocio en el menú lateral. El cambio se graba
 * en usuarios.sucursal (/api/sesion_cambiarsucursal), no solo en el navegador:
 * el backend usa esa columna para grabar cobros, gastos, etc. Después se
 * recarga la app para que todas las pantallas (que leen la sucursal al
 * montar) tomen la nueva; la sesión se restaura sola con el token.
 */
import { useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useGlobalMDAlert } from "context";
import { FP } from "components/financepro";
import { useApi, useSucursales } from "lib/api";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };

// Lista aparte para que el catálogo solo se pida al abrir el diálogo.
function ListaSucursales({ guardando, onElegir }) {
  const sucursales = useSucursales({ todas: true });
  const actual = String(localStorage.getItem("sucursal") || "")
    .split("-")[0]
    .trim();

  if (!sucursales.length) {
    return (
      <Typography
        sx={{ py: 3, textAlign: "center", color: FP.color.textSecondary, fontSize: "0.9rem" }}
      >
        Cargando sucursales…
      </Typography>
    );
  }
  return (
    <List dense sx={{ py: 0 }}>
      {sucursales.map((s) => {
        const label = String(s.label || "").trim();
        const codigo = label.split("-")[0].trim();
        const esActual = codigo === actual;
        return (
          <ListItemButton
            key={label}
            disabled={guardando}
            selected={esActual}
            onClick={() => (esActual ? null : onElegir(codigo))}
            sx={{ borderRadius: 1.5, mb: 0.5 }}
          >
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontWeight: esActual ? 700 : 500, fontSize: "0.92rem" }}
            />
            {esActual ? <CheckRoundedIcon sx={{ fontSize: 20, color: FP.color.navy }} /> : null}
          </ListItemButton>
        );
      })}
    </List>
  );
}

export default function CambiarSucursalDialog({ open, onClose }) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [guardando, setGuardando] = useState(false);

  const elegir = async (codigo) => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/sesion_cambiarsucursal", { sucursal: codigo });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cambiar la sucursal", color: "error" });
        setGuardando(false);
        return;
      }
      localStorage.setItem("sucursal", data.sucursal);
      localStorage.setItem("sucursalnombre", data.descripcion || "");
      window.location.reload();
    } catch (e) {
      showAlert({ message: "No se pudo cambiar la sucursal", color: "error" });
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
        Cambiar de sucursal
        {guardando ? <CircularProgress size={18} /> : null}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5, color: FP.color.textSecondary, fontSize: "0.85rem" }}>
          Lo que registre (cobros, gastos, ventas…) quedará en la sucursal que elija.
        </Typography>
        {open ? <ListaSucursales guardando={guardando} onElegir={elegir} /> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={guardando}
          sx={{
            ...btnBase,
            color: FP.color.textSecondary,
            "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
